import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { DateTime } from 'luxon';
import { apiGateway } from '../../../../config';

import type { AxiosResponse } from 'axios';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../../../../redux/store';
import type { ErrorResponse } from '../../../../@types/common';
import type { Factory, Item, Port } from '../../../../@types/common';
import type { Delivery, DeliveryHistory, DeliveryHistoryState } from '../../../../@types/delivery';

// ----------------------------------------------------------------------

const initialState: DeliveryHistoryState = {
  isLoading: false,
  hasError: false,
  hasSearched: false,
  message: undefined,
  options: {
    factory: undefined,
    item: undefined,
    port: undefined,
  },
  deliverys: undefined,
  dateFrom: DateTime.now().startOf('month').minus({months: 2}).toISO(),
  dateTo: DateTime.now().endOf('month').toISO(),
};

const slice = createSlice({
  name: 'deliveryHistory',
  initialState,
  reducers: {
    deliveryRest: () => initialState,
    startLoading(state: DeliveryHistoryState) {
      state.isLoading = true;
    },

    hasError(state: DeliveryHistoryState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.hasError = true;
      state.message = action.payload;
    },

    resetMessage(state: DeliveryHistoryState) {
      state.isLoading = false;
      state.hasError = false;
      state.message = '';
    },

// ----------------------------------------------------------------------

    hasSearched(state: DeliveryHistoryState, action: PayloadAction<boolean>) {
      state.hasSearched = action.payload;
    },

    changeStartDate(state: DeliveryHistoryState, action: PayloadAction<string | undefined>) {
      state.dateFrom = action.payload;
    },

    changeEndDate(state: DeliveryHistoryState, action: PayloadAction<string | undefined>) {
      state.dateTo = action.payload;
    },

// ----------------------------------------------------------------------

    getOptionsFactorySuccess(state: DeliveryHistoryState, action: PayloadAction<Factory[]>) {
      state.isLoading = false;
      state.options.factory = action.payload;
    },

    getOptionsItemSuccess(state: DeliveryHistoryState, action: PayloadAction<Item[]>) {
      state.isLoading = false;
      state.options.item = action.payload;
    },

    getOptionsPortSuccess(state: DeliveryHistoryState, action: PayloadAction<Port[]>) {
      state.isLoading = false;
      state.options.port = action.payload;
    },

// ----------------------------------------------------------------------

    getDeliveriesSuccess(state: DeliveryHistoryState, action: PayloadAction<DeliveryHistory[]>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
      state.deliverys = action.payload;
    },
  }
});

export default slice.reducer;


// ----------------------------------------------------------------------
// 日付変更+メッセージ
export function resetMessage(): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  }
};

export function changeStartDate(date: string | undefined): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeStartDate(date));
  }
};

export function changeEndDate(date: string | undefined): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeEndDate(date));
  }
};

// ----------------------------------------------------------------------
// グリッド項目変換用オプション取得
export function getOptionsFactory(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Factory[]>(
        apiGateway + '/options/factory',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsFactorySuccess(response.data));
      // console.log(response.data)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsItem(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Item[]>(
        apiGateway + '/options/item',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsItemSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

export function getOptionsPort(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Port[]>(
        apiGateway + '/options/port',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.getOptionsPortSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
};

// ----------------------------------------------------------------------
// 通常の出荷履歴検索
export function getDeliveries(dateFrom?: string, dateTo?: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Delivery[]>(
        apiGateway + '/deliveryHistory/search',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          params: {
            dateFrom,
            dateTo
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.hasSearched(true));
      dispatch(slice.actions.getDeliveriesSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
    // setTimeout(() => {
    //   dispatch(slice.actions.getDeliveriesSuccess([deliveryData[0]]));
    // } , 2000);
  };
};

//　OrderId単位での出荷履歴検索
export function getDelivery(id: string, dateFrom?: string, dateTo?: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Delivery[]>(
        apiGateway + '/deliveryHistory/search/' + id,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          params: {
            dateFrom,
            dateTo
          },
          timeout: 60000
        }
      );
      dispatch(slice.actions.hasSearched(true));
      dispatch(slice.actions.getDeliveriesSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
    // setTimeout(() => {
    //   dispatch(slice.actions.getDeliveriesSuccess([deliveryData[0]]));
    // } , 2000);
  };
};

// ----------------------------------------------------------------------
